import { Icon } from '@zapier/design-system';
import clsx from 'clsx';
import React, { forwardRef, useRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { Button, IconButton, FilterChipList, FilterChip, FilterChipIcon } from '@zapier/design-system-beta';
import { AutogrowTextarea } from './AutogrowTextarea.es.js';

const copilotInputContainer = "_copilotInputContainer_1406m_1";
const loading = "_loading_1406m_12";
const copilotInput = "_copilotInput_1406m_1";
const inputRow = "_inputRow_1406m_22";
const textarea = "_textarea_1406m_27";
const chipsRow = "_chipsRow_1406m_39";
const controlsRow = "_controlsRow_1406m_42";
const actionStartButtons = "_actionStartButtons_1406m_49";
const actionStartButton = "_actionStartButton_1406m_49";
const submitArea = "_submitArea_1406m_57";
const actionEndButton = "_actionEndButton_1406m_62";
const styles = {
	copilotInputContainer: copilotInputContainer,
	loading: loading,
	copilotInput: copilotInput,
	inputRow: inputRow,
	textarea: textarea,
	chipsRow: chipsRow,
	controlsRow: controlsRow,
	actionStartButtons: actionStartButtons,
	actionStartButton: actionStartButton,
	submitArea: submitArea,
	actionEndButton: actionEndButton
};

const CopilotInput = forwardRef(({ input, handleInputChange, handleSubmit, placeholder, status = "ready", className, style, actions = [], stop, chips }, ref) => {
  const textareaRef = useRef(null);
  useImperativeHandle(ref, () => textareaRef.current);
  const isLoading = status === "submitted" || status === "streaming";
  const handleChange = useCallback((e) => {
    handleInputChange(e);
  }, [handleInputChange]);
  const onSubmit = useCallback((e) => {
    if (e)
      e.preventDefault();
    if (input.trim() && !isLoading) {
      handleSubmit();
    }
  }, [input, isLoading, handleSubmit]);
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }, [onSubmit]);
  const focusInput = useCallback(() => {
    var _a;
    (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, []);
  const onRemove = useCallback((ids) => {
    Array.from(ids).forEach((id) => {
      var _a;
      const chip = chips === null || chips === void 0 ? void 0 : chips.find((c) => c.id === id);
      (_a = chip === null || chip === void 0 ? void 0 : chip.onRemove) === null || _a === void 0 ? void 0 : _a.call(chip, id);
    });
  }, [chips]);
  const actionsStart = useMemo(() => (actions !== null && actions !== void 0 ? actions : []).filter((action) => action.location === "start"), [actions]);
  const actionsEnd = useMemo(() => (actions !== null && actions !== void 0 ? actions : []).filter((action) => action.location === "end"), [actions]);
  const submitArea = useMemo(() => {
    if (isLoading && stop) {
      return React.createElement(Button, { type: "button", variant: "primary", size: "small", iconStart: React.createElement(Icon, { name: "statusStop", size: "small" }), onPress: stop, "data-zds": true }, "Stop");
    }
    if (isLoading) {
      return React.createElement(IconButton, { name: "arrowBigUp", variant: "primary", size: "small", "aria-label": "Loading...", "data-zds": true, isDisabled: true });
    }
    return React.createElement(IconButton, { name: "arrowBigUp", variant: "primary", size: "small", isDisabled: !input.trim(), "data-zds": true, onPress: () => onSubmit() });
  }, [isLoading, stop, input, onSubmit]);
  return React.createElement(
    "div",
    { className: clsx(styles.copilotInputContainer, className, {
      [styles.loading]: isLoading
    }), style },
    React.createElement(
      "form",
      { className: styles.copilotInput, onClick: focusInput, onSubmit, "data-zds": true },
      React.createElement(
        "div",
        { className: styles.inputRow },
        React.createElement(AutogrowTextarea, { className: styles.textarea, ref: textareaRef, value: input, placeholder: placeholder ? placeholder : isLoading ? "Thinking..." : "Type a message...", rows: 1, disabled: isLoading, onChange: handleChange, onKeyDown: handleKeyDown, "data-zds": true })
      ),
      chips && chips.length > 0 && React.createElement(
        "div",
        { className: styles.chipsRow },
        React.createElement(FilterChipList, { "aria-label": "Filter options", variant: "primary", size: "compact", items: chips, onRemove }, (item) => {
          const originalChip = chips === null || chips === void 0 ? void 0 : chips.find((chip) => chip.id === item.id);
          const icon = originalChip === null || originalChip === void 0 ? void 0 : originalChip.icon;
          return React.createElement(FilterChip, { id: item.id, title: item.title }, icon && (typeof icon === "string" ? React.createElement(FilterChipIcon, { name: icon }) : icon));
        })
      ),
      React.createElement(
        "div",
        { className: styles.controlsRow },
        React.createElement("div", { className: styles.actionStartButtons }, actionsStart.map((action) => {
          var _a;
          return React.createElement(Button, { className: styles.actionStartButton, key: action.text, type: "button", variant: action.active ? "secondary" : "tertiary", size: "small", iconStart: typeof action.icon === "string" ? React.createElement(Icon, { name: action.icon, size: "small" }) : action.icon, onPress: () => action.onClick(action.id), isDisabled: isLoading, "aria-label": (_a = action["aria-label"]) !== null && _a !== void 0 ? _a : action.text, "data-zds": true }, action.text);
        })),
        React.createElement(
          "div",
          { className: styles.submitArea },
          actionsEnd.map((action) => React.createElement(Button, { className: styles.actionEndButton, key: `${action["aria-label"]}-${typeof action.icon === "string" ? action.icon : "custom"}`, type: "button", variant: action.active ? "secondary" : "tertiary", size: "small", iconStart: typeof action.icon === "string" ? React.createElement(Icon, { name: action.icon, size: "small" }) : action.icon, onPress: () => action.onClick(action.id), isDisabled: isLoading, "aria-label": action["aria-label"], "data-zds": true }, " ")),
          submitArea
        )
      )
    )
  );
});
CopilotInput.displayName = "CopilotInput";

export { CopilotInput, CopilotInput as default };
//# sourceMappingURL=CopilotInput.es.js.map
